/**
 * useChatCore — the ONE parameterized chat engine behind useChat,
 * useWilliamsChat (#931).
 *
 * The three channel hooks were near-identical reducer clones around the
 * shared useSSEConnection: the same base reducer, the same conversationId
 * ref sync, the same optimistic send / history-load / switch-conversation
 * machinery, differing only in which API module they called and which SSE
 * path they connected to. This hook owns all of that once; the channel
 * hooks are thin adapters that supply the channel parameters and (for the
 * companion) layer their channel-specific state on top.
 *
 * Parameter stability contract:
 *   - `ssePathPrefix` and `customEvents` configure useSSEConnection and MUST
 *     be render-stable (string literal / module-level const) — a fresh array
 *     identity per render would tear the EventSource down every render.
 *   - Everything else (`getHistory`, `send`, `onMessage`, `onCustomEvent`,
 *     `onSwitchReset`, `enabled`) is read through a ref at call time, so
 *     inline closures are fine and never churn callback identities.
 */

import { useReducer, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSSEConnection, type SSEHandlers } from './useSSEConnection';
import type { ChatMessageState, SSEChipsPayload } from '@/types/chat';
import type { ChatCopyPack } from '@core/config/types';

// ─── State ──────────────────────────────────────────────────────

export type ChatConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

/** #1902: the recall payload the SSE `memory_recall` frame carries. */
export interface MemoryRecallState {
  sourceCount: number;
  sources?: Array<{ kind: string; label: string }>;
}

export interface ChatCoreState {
  messages: ChatMessageState[];
  status: ChatConnectionStatus;
  isTyping: boolean;
  isStreaming: boolean;
  error: string | null;
  conversationId: string;
  // ── chat/voice parity (#1731, F-4): the cpv3 engine's responsibilities ──
  /** The thread was ended (by the user, a closing turn or the sweeper); sends are refused. */
  sessionEnded: boolean;
  /** History said this thread carries voice turns (`isVoiceConversation`). */
  isVoiceThread: boolean;
  /** History said regeneration is on for this thread (`versioningEnabled`). */
  versioningEnabled: boolean;
  /**
   * The mount-time history fetch has answered — with turns, with none, or
   * with a rejection. A surface that shows a blank-slate greeting only on an
   * empty thread waits on this for a resumed thread, so it never stamps the
   * greeting above history that is still on the wire (2026-09-17).
   */
  historySettled: boolean;
  /** Suggestion chips for the latest reply; cleared on every send. */
  chips: string[];
  /**
   * #1902: what the companion actually remembered for the latest reply, or
   * null. Engine state rather than a field on a message, deliberately: the
   * host stages assistant replies as split bubbles with derived ids, so a
   * payload attached to the source row reaches none of them (#1908) — and
   * copying it onto all of them would draw one recall line per bubble.
   * Cleared on every send, like chips.
   */
  recall: MemoryRecallState | null;
  /** Classified failure of the last send/stream, as a copy-table key. */
  failure: FailureReport | null;
  /** Increments on every send — the autoscroll latch resets on it. */
  reanchorKey: number;
}

/**
 * What a channel's history fetch may return: the bare rows (every existing
 * adapter) or the rows plus the thread flags the backend learns in phases 6
 * and 8. Flags default to false when absent.
 */
export interface HistoryResult {
  messages: ChatMessageState[];
  isVoiceConversation?: boolean;
  versioningEnabled?: boolean;
}

function normalizeHistory(result: ChatMessageState[] | HistoryResult): Required<HistoryResult> {
  if (Array.isArray(result)) {
    return { messages: result, isVoiceConversation: false, versioningEnabled: false };
  }
  return {
    messages: result.messages ?? [],
    isVoiceConversation: Boolean(result.isVoiceConversation),
    versioningEnabled: Boolean(result.versioningEnabled),
  };
}

// ─── Failure classification (port of cpv3 classifyStevensFailure) ───

export type FailureStage = 'connect' | 'send' | 'stream';

/**
 * A failure as a COPY KEY, not a string: the hook stays voice-free, and the
 * surface resolves `copy.failures[key]` (with `variant` picking the sub-slot)
 * from the active ChatSurfaceConfig. `retryable` gates the retry affordance.
 */
export interface FailureReport {
  /** Stable machine code — greppable in console logs and support tickets. */
  code: string;
  key: keyof ChatCopyPack['failures'];
  stage: FailureStage;
  hadPartialContent: boolean;
  retryable: boolean;
  /** HTTP status when the transport surfaced one. */
  status?: string;
  /** The upstream reason when one was given (agent error, refusal body). */
  reason?: string;
}

/** Reasons TRF's transport layer emits itself (useSSEConnection / chatApi). */
const TIMEOUT_RE = /\b(timeout|timed out)\b/i;
const CONNECTION_LOST_RE = /\b(connection (lost|dropped|closed|never opened)|network (down|error|failure)|failed to fetch)\b/i;
const REFUSED_RE = /^Stream refused \(HTTP (\d{3})(?: ([A-Z_]+))?\): ([\s\S]*)$/;
const HTTP_STATUS_RE = /HTTP (\d{3})/;

export function classifyFailure(
  stage: FailureStage,
  rawMessage: string,
  hadPartialContent: boolean,
): FailureReport {
  const raw = (rawMessage || '').trim();
  const base = { stage, hadPartialContent };

  if (TIMEOUT_RE.test(raw)) {
    return { ...base, code: 'CHAT_STREAM_TIMEOUT', key: 'streamTimeout', retryable: true };
  }

  const refused = REFUSED_RE.exec(raw);
  if (refused) {
    const [, status, serverCode, reasonRaw] = refused;
    if (serverCode === 'MESSAGE_TOO_LONG' || status === '413') {
      return { ...base, code: 'CHAT_REFUSED_MESSAGE_TOO_LONG', key: 'refusedMessageTooLong', retryable: false, status };
    }
    return {
      ...base,
      code: `CHAT_REFUSED_${serverCode || status}`,
      key: 'refused',
      retryable: false,
      status,
      reason: reasonRaw.trim(),
    };
  }

  if (CONNECTION_LOST_RE.test(raw)) {
    return stage === 'connect'
      ? { ...base, code: 'CHAT_STREAM_NOT_OPENED', key: 'notOpened', retryable: true }
      : { ...base, code: 'CHAT_STREAM_INTERRUPTED', key: 'interrupted', retryable: true };
  }

  if (stage === 'send') {
    const status = HTTP_STATUS_RE.exec(raw)?.[1];
    if (status === '413') {
      return { ...base, code: 'CHAT_REFUSED_MESSAGE_TOO_LONG', key: 'refusedMessageTooLong', retryable: false, status };
    }
    return {
      ...base,
      code: status ? `CHAT_SEND_HTTP_${status}` : 'CHAT_SEND_FAILED',
      key: 'sendRejected',
      retryable: true,
      status,
    };
  }

  if (stage === 'stream') {
    // A reason the AGENT reported over a live stream — the one case where we
    // know what went wrong and know the agent was on the other end.
    return { ...base, code: 'CHAT_AGENT_ERROR', key: 'agentError', retryable: true, reason: raw || undefined };
  }

  return { ...base, code: 'CHAT_CONNECT_FAILED', key: 'connectFailed', retryable: true, reason: raw || undefined };
}

type ChatCoreAction =
  | { type: 'SET_CONVERSATION_ID'; payload: string }
  | { type: 'SET_STATUS'; payload: ChatConnectionStatus }
  | { type: 'SET_TYPING'; payload: boolean }
  | { type: 'SET_STREAMING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'ADD_MESSAGE'; payload: ChatMessageState }
  | { type: 'UPDATE_MESSAGE'; payload: { id: string; updates: Partial<ChatMessageState> } }
  | { type: 'SET_MESSAGES'; payload: ChatMessageState[] }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'HISTORY_LOADED'; payload: Required<HistoryResult> }
  | { type: 'HISTORY_SETTLED' }
  | { type: 'SESSION_ENDED' }
  | { type: 'SESSION_RESUMED' }
  | { type: 'CHIPS'; payload: string[] }
  | { type: 'RECALL'; payload: MemoryRecallState | null }
  | { type: 'FAILURE'; payload: FailureReport | null }
  | { type: 'REANCHOR' };

function chatCoreReducer(state: ChatCoreState, action: ChatCoreAction): ChatCoreState {
  switch (action.type) {
    case 'SET_CONVERSATION_ID':
      return { ...state, conversationId: action.payload };
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_TYPING':
      return { ...state, isTyping: action.payload };
    case 'SET_STREAMING':
      return { ...state, isStreaming: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.payload.id ? { ...m, ...action.payload.updates } : m
        ),
      };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'CLEAR_MESSAGES':
      return { ...state, messages: [] };
    case 'HISTORY_LOADED':
      return {
        ...state,
        messages: action.payload.messages,
        isVoiceThread: action.payload.isVoiceConversation,
        versioningEnabled: action.payload.versioningEnabled,
      };
    case 'HISTORY_SETTLED':
      return { ...state, historySettled: true };
    case 'SESSION_ENDED':
      return { ...state, sessionEnded: true, isTyping: false, isStreaming: false };
    case 'SESSION_RESUMED':
      return { ...state, sessionEnded: false };
    case 'RECALL':
      return { ...state, recall: action.payload };
    case 'CHIPS':
      return { ...state, chips: action.payload };
    case 'FAILURE':
      return { ...state, failure: action.payload };
    case 'REANCHOR':
      return { ...state, reanchorKey: state.reanchorKey + 1 };
    default:
      return state;
  }
}

// ─── Options ────────────────────────────────────────────────────

/**
 * Imperative escape hatch handed to the channel extensions so a channel can
 * inject messages into the shared transcript (handoff system pills), patch
 * one in place (per-message retranslate) or drop the streaming indicators
 * (`ai_cancelled`) without owning the reducer. All functions are
 * identity-stable for the life of the hook.
 */
export interface ChatCoreHelpers {
  addMessage: (message: ChatMessageState) => void;
  updateMessage: (id: string, updates: Partial<ChatMessageState>) => void;
  setMessages: (messages: ChatMessageState[]) => void;
  clearMessages: () => void;
  setTyping: (typing: boolean) => void;
  setStreaming: (streaming: boolean) => void;
  setError: (error: string | null) => void;
}

export interface UseChatCoreOptions<E = undefined> {
  /** SSE URL path prefix, e.g. '/api/chat/stream'. MUST be render-stable. */
  ssePathPrefix: string;
  /** Extra named SSE events forwarded to onCustomEvent. MUST be render-stable. */
  customEvents?: string[];
  /** Gate for SSE auto-connect (e.g. Williams' sessionReady). Defaults true. */
  enabled?: boolean;
  /** Channel history fetch — seeds the transcript on mount and on switch. Rows, or rows + thread flags. */
  getHistory: (conversationId: string) => Promise<ChatMessageState[] | HistoryResult>;
  /**
   * Channel regeneration call (#1731 F-4; wired by phase 8). Invoked by
   * `retryMessage` only for the LAST assistant turn and only when history
   * said `versioningEnabled`; the regenerated reply arrives over the stream.
   */
  retry?: (conversationId: string, messageId: string) => Promise<void>;
  /** Channel send POST. `extra` is the per-call payload (companion page_context). */
  send: (conversationId: string, content: string, extra?: E) => Promise<void>;
  /** Channel side effects per final assistant message (companion active-agent tracking). */
  onMessage?: (message: ChatMessageState, helpers: ChatCoreHelpers) => void;
  /** Channel handling for named custom events AND default-framed handoff payloads. */
  onCustomEvent?: (eventName: string, data: unknown, helpers: ChatCoreHelpers) => void;
  /** Extra channel-state reset when the conversation switches. */
  onSwitchReset?: () => void;
}

// ─── Hook ───────────────────────────────────────────────────────

export function useChatCore<E = undefined>(
  initialConversationId: string,
  options: UseChatCoreOptions<E>,
) {
  const [state, dispatch] = useReducer(chatCoreReducer, {
    messages: [],
    status: 'idle',
    isTyping: false,
    isStreaming: false,
    error: null,
    conversationId: initialConversationId,
    sessionEnded: false,
    isVoiceThread: false,
    versioningEnabled: false,
    historySettled: false,
    chips: [],
    recall: null,
    failure: null,
    reanchorKey: 0,
  });

  // Whether chunks are on the wire right now — read at failure time so a
  // stream error classifies as "interrupted after partial content" rather
  // than "never opened". A ref, not state: the SSE handlers are memoized.
  const streamingRef = useRef(false);

  // sessionEnded is read inside the identity-stable sendMessage.
  const sessionEndedRef = useRef(false);
  useEffect(() => {
    sessionEndedRef.current = state.sessionEnded;
  }, [state.sessionEnded]);

  // Keep conversationId ref in sync for callbacks
  const conversationIdRef = useRef(state.conversationId);
  useEffect(() => {
    conversationIdRef.current = state.conversationId;
  }, [state.conversationId]);

  // Channel parameters are read through a ref at call time so adapters can
  // pass inline closures without churning any callback identity here.
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Stable imperative surface for the channel extensions (dispatch is stable).
  const helpers: ChatCoreHelpers = useMemo(() => ({
    addMessage: (message) => dispatch({ type: 'ADD_MESSAGE', payload: message }),
    updateMessage: (id, updates) => dispatch({ type: 'UPDATE_MESSAGE', payload: { id, updates } }),
    setMessages: (messages) => dispatch({ type: 'SET_MESSAGES', payload: messages }),
    clearMessages: () => dispatch({ type: 'CLEAR_MESSAGES' }),
    setTyping: (typing) => dispatch({ type: 'SET_TYPING', payload: typing }),
    setStreaming: (streaming) => dispatch({ type: 'SET_STREAMING', payload: streaming }),
    setError: (error) => dispatch({ type: 'SET_ERROR', payload: error }),
  }), []);

  // Bridge SSE events to the reducer, then hand off to the channel extensions.
  const sseHandlers: SSEHandlers = useMemo(() => ({
    onStatusChange: (status) => dispatch({ type: 'SET_STATUS', payload: status }),
    onTyping: (typing) => dispatch({ type: 'SET_TYPING', payload: typing }),
    onStreamingChange: (streaming) => {
      streamingRef.current = streaming;
      dispatch({ type: 'SET_STREAMING', payload: streaming });
    },
    onError: (error) => {
      dispatch({ type: 'SET_ERROR', payload: error });
      dispatch({
        type: 'FAILURE',
        payload: classifyFailure(streamingRef.current ? 'stream' : 'connect', error, streamingRef.current),
      });
    },
    onMessage: (message) => {
      dispatch({ type: 'ADD_MESSAGE', payload: message });
      optionsRef.current.onMessage?.(message, helpers);
    },
    onCustomEvent: (eventName, data) => {
      // #1731 F-4: chips are engine state; the frame still reaches the channel.
      if (eventName === 'ui_chips') {
        const chips = (data as SSEChipsPayload | undefined)?.chips;
        dispatch({ type: 'CHIPS', payload: Array.isArray(chips) ? chips.filter((c) => typeof c === 'string') : [] });
      }
      // #1902: the backend only sends this frame when something material was
      // recalled, so arrival is the signal — there is no "recalled nothing"
      // payload to render.
      if (eventName === 'memory_recall') {
        const payload = data as MemoryRecallState | undefined;
        const sourceCount = Number(payload?.sourceCount);
        dispatch({
          type: 'RECALL',
          payload:
            Number.isFinite(sourceCount) && sourceCount > 0
              ? { sourceCount, sources: Array.isArray(payload?.sources) ? payload.sources : undefined }
              : null,
        });
      }
      optionsRef.current.onCustomEvent?.(eventName, data, helpers);
    },
  }), [helpers]);

  const { connect, disconnect } = useSSEConnection(
    state.conversationId,
    sseHandlers,
    {
      ssePathPrefix: options.ssePathPrefix,
      enabled: options.enabled,
      customEvents: options.customEvents,
    },
  );

  // Load history for the initial conversation on mount. switchConversation()
  // fetches history when switching, but the initial conversationId never goes
  // through that path. Guarded on a truthy id: the widget mounts the companion
  // with useChat('') and only mints an id on first open.
  useEffect(() => {
    if (!initialConversationId) {
      dispatch({ type: 'HISTORY_SETTLED' });
      return;
    }
    optionsRef.current.getHistory(initialConversationId).then((result) => {
      const history = normalizeHistory(result);
      if (history.messages.length > 0 || history.isVoiceConversation || history.versioningEnabled) {
        dispatch({ type: 'HISTORY_LOADED', payload: history });
      }
    }).catch(() => {
      // New or unknown conversation — no history to load
    }).finally(() => {
      dispatch({ type: 'HISTORY_SETTLED' });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount — initialConversationId is intentionally frozen

  // ─── Actions ──────────────────────────────────────────────────

  const sendMessage = useCallback(async (content: string, extra?: E) => {
    // #1731 F-4: an ended thread takes no new turns; the surface shows the
    // banner (copy from the active config) and offers resume/new instead.
    if (sessionEndedRef.current) return;

    // Clear any previous error when user sends a new message
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'FAILURE', payload: null });
    dispatch({ type: 'CHIPS', payload: [] });
    dispatch({ type: 'RECALL', payload: null });
    dispatch({ type: 'REANCHOR' });

    const userMessage: ChatMessageState = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
      status: 'sending',
    };

    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });

    try {
      await optionsRef.current.send(conversationIdRef.current, content, extra);
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { id: userMessage.id, updates: { status: 'complete' } },
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Couldn’t send your message. Please try again.';
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { id: userMessage.id, updates: { status: 'error' } },
      });
      dispatch({ type: 'SET_ERROR', payload: message });
      dispatch({ type: 'FAILURE', payload: classifyFailure('send', message, false) });
    }
  }, []);

  const switchConversation = useCallback(
    async (newConversationId: string) => {
      // 1. Disconnect SSE
      disconnect();

      // 2. Reset state (channel-specific resets ride onSwitchReset)
      dispatch({ type: 'CLEAR_MESSAGES' });
      dispatch({ type: 'SET_CONVERSATION_ID', payload: newConversationId });
      dispatch({ type: 'SET_ERROR', payload: null });
      dispatch({ type: 'FAILURE', payload: null });
      dispatch({ type: 'CHIPS', payload: [] });
      dispatch({ type: 'SESSION_RESUMED' });
      optionsRef.current.onSwitchReset?.();
      conversationIdRef.current = newConversationId;
      sessionEndedRef.current = false;

      // 3. Load history
      try {
        const history = normalizeHistory(await optionsRef.current.getHistory(newConversationId));
        dispatch({ type: 'HISTORY_LOADED', payload: history });
      } catch {
        // Conversation might be new (no history)
      }

      // 4. Reconnect SSE
      connect();
    },
    [disconnect, connect]
  );

  const startNewConversation = useCallback(() => {
    const newId = crypto.randomUUID();
    switchConversation(newId);
    return newId;
  }, [switchConversation]);

  const clearMessages = useCallback(() => {
    dispatch({ type: 'CLEAR_MESSAGES' });
  }, []);

  const dismissError = useCallback(() => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  // B-597: a user-driven retry after the stream failed or stalled. Tears the
  // old EventSource (and any pending backoff timer) down first so `connect`'s
  // "already connected" early return cannot swallow the retry.
  const reconnect = useCallback(() => {
    disconnect();
    dispatch({ type: 'SET_ERROR', payload: null });
    connect();
  }, [connect, disconnect]);

  // ─── #1731 F-4 actions ────────────────────────────────────────

  const markSessionEnded = useCallback(() => {
    sessionEndedRef.current = true;
    dispatch({ type: 'SESSION_ENDED' });
  }, []);

  const markSessionResumed = useCallback(() => {
    sessionEndedRef.current = false;
    dispatch({ type: 'SESSION_RESUMED' });
  }, []);

  /** Refetch history for the current conversation and replace the transcript. */
  const reloadMessages = useCallback(async () => {
    const history = normalizeHistory(await optionsRef.current.getHistory(conversationIdRef.current));
    dispatch({ type: 'HISTORY_LOADED', payload: history });
  }, []);

  // Read by retryMessage without churning its identity.
  const stateRef = useRef(state);
  stateRef.current = state;

  /**
   * Regenerate ONE assistant turn: the conversation's last one, and only
   * when history said versioning is on (spec §1.6). Anything else is a no-op.
   */
  const retryMessage = useCallback(async (messageId: string) => {
    const { messages, versioningEnabled } = stateRef.current;
    if (!versioningEnabled) return;
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant || lastAssistant.id !== messageId) return;
    const retry = optionsRef.current.retry;
    if (!retry) return;
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'FAILURE', payload: null });
    dispatch({ type: 'CHIPS', payload: [] });
    try {
      await retry(conversationIdRef.current, messageId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Couldn’t regenerate that reply.';
      dispatch({ type: 'SET_ERROR', payload: message });
      dispatch({ type: 'FAILURE', payload: classifyFailure('send', message, false) });
    }
  }, []);

  // ─── Return ───────────────────────────────────────────────────

  return {
    messages: state.messages,
    status: state.status,
    isTyping: state.isTyping,
    isStreaming: state.isStreaming,
    error: state.error,
    conversationId: state.conversationId,
    sendMessage,
    switchConversation,
    startNewConversation,
    clearMessages,
    dismissError,
    reconnect,
    /** Imperative transcript access for channel adapters (retry, handoff pills). */
    helpers,
    // ── #1731 F-4 ──
    sessionEnded: state.sessionEnded,
    markSessionEnded,
    markSessionResumed,
    isVoiceThread: state.isVoiceThread,
    versioningEnabled: state.versioningEnabled,
    historySettled: state.historySettled,
    reloadMessages,
    retryMessage,
    chips: state.chips,
    recall: state.recall,
    failure: state.failure,
    reanchorKey: state.reanchorKey,
  };
}
